"use client";

import { track } from "@/lib/analytics/client";
import type { Events } from "@/lib/analytics/events";
import { getClientConsent } from "@/lib/analytics/consent";
import { hasFired, markFired } from "@/lib/analytics/oncePerTab";
import { CONSENT_CHANGE_EVENT } from "@/hooks/useCookieConsent";
import { useEffect, useEffectEvent, useRef } from "react";

type Options = {
  /** Report again when this changes, such as an event id. Defaults to `event`. */
  key?: string;
  /**
   * Also remember `key` in `sessionStorage`, so the event is sent once per
   * browser tab and not once per mount. Without it, a new mount reports again.
   */
  oncePerTab?: boolean;
};

/**
 * Tracks `event` once per `key`, after the visitor has accepted analytics
 * consent.
 *
 * `getProps` runs on mount, and again when `key` changes, so the props describe
 * the page the key first appeared on. A visitor who accepts consent pages later
 * is still reported with those props. The hook listens for
 * `CONSENT_CHANGE_EVENT`, and marks the key as reported only when it tracks.
 */
export function useTrackOnceConsented<EventType extends keyof Events>(
  event: EventType,
  getProps: () => Events[EventType],
  { key = event, oncePerTab = false }: Options = {},
): void {
  const readProps = useEffectEvent(getProps);

  const reportedKey = useRef<string | null>(null);
  useEffect(() => {
    const props = readProps();

    const report = () => {
      if (reportedKey.current === key) return;
      if (oncePerTab && hasFired(key)) return;
      if (getClientConsent() !== "accepted") return;
      reportedKey.current = key;
      if (oncePerTab) markFired(key);
      track(event, props);
    };

    report();
    window.addEventListener(CONSENT_CHANGE_EVENT, report);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, report);
  }, [event, key, oncePerTab]);
}
